import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Upload, Trash2, Download, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/mafia/DashboardLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 1_000_000;

export default function DashboardSettingsPage() {
  const navigate = useNavigate();
  const { gamer, loading, refresh, setGamer } = useMafiaAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const avatarInput = useRef(null);
  const coverInput = useRef(null);

  useEffect(() => { if (!loading && !gamer) navigate('/mafia/login', { replace: true }); }, [loading, gamer, navigate]);

  useEffect(() => {
    if (gamer) {
      setForm({
        name: gamer.name || '',
        bio: gamer.bio || '',
        custom_avatar: gamer.custom_avatar || '',
        cover_url: gamer.cover_url || '',
        language: gamer.language || 'en',
        privacy_public_profile: gamer.privacy_public_profile ?? true,
        privacy_show_email: gamer.privacy_show_email ?? false,
      });
    }
  }, [gamer]);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) { reject(new Error('Image too large (max 1MB)')); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickImage = async (e, field) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f);
      setForm((p) => ({ ...p, [field]: dataUrl }));
    } catch (err) { toast.error(err.message || 'Could not load image'); }
    finally { e.target.value = ''; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/me`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Save failed');
      }
      const updated = await r.json();
      setGamer && setGamer(updated);
      toast.success('Profile updated');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const exportData = async () => {
    try {
      const r = await fetch(`${API}/api/mafia/auth/me/export`, { credentials: 'include' });
      if (!r.ok) throw new Error('Export failed');
      const data = await r.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mafia-salon-export-${gamer.member_no}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (e) { toast.error(e.message); }
  };

  const deleteAccount = async () => {
    try {
      const r = await fetch(`${API}/api/mafia/auth/me`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Account deleted');
      await refresh();
      navigate('/', { replace: true });
    } catch (e) { toast.error(e.message); }
  };

  if (loading || !form) {
    return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  }

  return (
    <DashboardLayout
      title="Settings"
      subtitle="Profile & preferences"
      actions={
        <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="ms-btn ms-btn-primary disabled:opacity-50">
          <Save size={13} /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      }
    >
      <div className="max-w-3xl space-y-6" data-testid="settings-page">
        <Section title="Profile" subtitle="How others see you across the Salon.">
          <Field label="Display name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="settings-name"
              className="ms-input"
            />
          </Field>
          <Field label="Bio" hint={`${(form.bio || '').length}/280`}>
            <textarea
              value={form.bio || ''}
              onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 280) })}
              maxLength={280}
              rows={3}
              data-testid="settings-bio"
              placeholder="Tell other players about your play style…"
              className="ms-input resize-none"
            />
          </Field>

          <Field label="Avatar" hint="JPG/PNG, max 1MB. Overrides your Google picture.">
            <div className="flex items-center gap-4">
              {(form.custom_avatar || gamer.picture) ? (
                <img src={form.custom_avatar || gamer.picture} alt="" className="w-16 h-16 object-cover" style={{ borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }} />
              ) : (
                <div className="w-16 h-16 flex items-center justify-center ms-text-3 text-xs" style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px dashed var(--ms-border-strong)' }}>No image</div>
              )}
              <input ref={avatarInput} type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e, 'custom_avatar')} data-testid="settings-avatar-input" />
              <button type="button" onClick={() => avatarInput.current?.click()} data-testid="settings-avatar-upload" className="ms-btn ms-btn-secondary ms-btn-sm">
                <Upload size={12} /> Upload
              </button>
              {form.custom_avatar && (
                <button type="button" onClick={() => setForm({ ...form, custom_avatar: '' })} data-testid="settings-avatar-clear" className="ms-btn ms-btn-ghost ms-btn-sm">
                  Remove
                </button>
              )}
            </div>
          </Field>

          <Field label="Cover photo" hint="The banner shown at the top of your public profile.">
            <div className="space-y-3">
              {form.cover_url ? (
                <div className="w-full h-36 overflow-hidden" style={{ borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }}>
                  <img src={form.cover_url} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-full h-24 flex items-center justify-center ms-text-3 text-xs" style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px dashed var(--ms-border-strong)' }}>No cover uploaded</div>
              )}
              <div className="flex items-center gap-2">
                <input ref={coverInput} type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e, 'cover_url')} data-testid="settings-cover-input" />
                <button type="button" onClick={() => coverInput.current?.click()} data-testid="settings-cover-upload" className="ms-btn ms-btn-secondary ms-btn-sm">
                  <Upload size={12} /> {form.cover_url ? 'Replace' : 'Upload'} cover
                </button>
                {form.cover_url && (
                  <button type="button" onClick={() => setForm({ ...form, cover_url: '' })} data-testid="settings-cover-clear" className="ms-btn ms-btn-ghost ms-btn-sm">Remove</button>
                )}
              </div>
            </div>
          </Field>
        </Section>

        <Section title="Preferences">
          <Field label="Language">
            <div className="flex gap-1 ms-surface-2 ms-border p-1 w-fit" style={{ borderRadius: 'var(--ms-radius-md)' }} data-testid="settings-language">
              {[
                { k: 'en', l: 'English' },
                { k: 'ru', l: 'Русский' },
                { k: 'ua', l: 'Українська' },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setForm({ ...form, language: opt.k })}
                  data-testid={`lang-${opt.k}`}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${form.language === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
                  style={form.language === opt.k ? { background: 'var(--ms-surface-3)', borderRadius: 'var(--ms-radius-sm)' } : {}}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Privacy">
          <Toggle label="Public profile" hint="When off, only you can view your profile page." value={form.privacy_public_profile} onChange={(v) => setForm({ ...form, privacy_public_profile: v })} testid="toggle-public" />
          <Toggle label="Show email on profile" hint="Display your email address publicly." value={form.privacy_show_email} onChange={(v) => setForm({ ...form, privacy_show_email: v })} testid="toggle-email" />
        </Section>

        <Section title="Your data">
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm ms-text">Export account data</p>
              <p className="text-xs ms-text-2 mt-1">Download a JSON with profile, comments, follows, and games.</p>
            </div>
            <button type="button" onClick={exportData} data-testid="settings-export-btn" className="ms-btn ms-btn-secondary ms-btn-sm shrink-0">
              <Download size={12} /> Export
            </button>
          </div>
        </Section>

        {/* Danger zone */}
        <section className="ms-card" style={{ borderColor: 'rgba(220,38,38,0.35)' }}>
          <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(220,38,38,0.25)' }}>
            <AlertTriangle size={14} className="ms-text-red" />
            <p className="ms-kicker ms-kicker-red">Danger zone</p>
          </div>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm ms-text">Delete account permanently</p>
              <p className="text-xs ms-text-2 mt-1">Removes profile, comments, follows, sessions. Match records are anonymised. Cannot be undone.</p>
            </div>
            {confirmDelete ? (
              <div className="flex gap-2 shrink-0">
                <button type="button" onClick={() => setConfirmDelete(false)} className="ms-btn ms-btn-secondary ms-btn-sm">Cancel</button>
                <button type="button" onClick={deleteAccount} data-testid="settings-delete-confirm" className="ms-btn ms-btn-danger ms-btn-sm">
                  <Check size={12} /> Confirm delete
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} data-testid="settings-delete-btn" className="ms-btn ms-btn-danger ms-btn-sm shrink-0">
                <Trash2 size={12} /> Delete account
              </button>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="ms-card">
      <div className="px-5 py-4 ms-border-b">
        <p className="ms-kicker">{title}</p>
        {subtitle && <p className="text-xs ms-text-2 mt-1">{subtitle}</p>}
      </div>
      <div className="ms-divide">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="px-5 py-4 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium ms-text">{label}</label>
        {hint && <span className="text-[11px] ms-text-3">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, hint, value, onChange, testid }) {
  return (
    <div className="px-5 py-4 flex items-start justify-between gap-4">
      <div>
        <p className="text-sm ms-text font-medium">{label}</p>
        {hint && <p className="text-xs ms-text-2 mt-1">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        className="shrink-0 w-11 h-6 flex items-center transition-colors"
        style={{
          background: value ? 'var(--ms-red)' : 'var(--ms-surface-3)',
          borderRadius: '999px',
          padding: '2px',
          justifyContent: value ? 'flex-end' : 'flex-start',
        }}
      >
        <span className="w-4 h-4 bg-white block" style={{ borderRadius: '999px' }} />
      </button>
    </div>
  );
}
